import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CompressIcon from '@mui/icons-material/Compress';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import ImageIcon from '@mui/icons-material/Image';
import EditIcon from '@mui/icons-material/Edit';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  TransformWrapper,
  TransformComponent,
  type ReactZoomPanPinchContentRef,
} from 'react-zoom-pan-pinch';
import { MarkdownTextarea } from './MarkdownTextarea';
import { normalizeMediaUrl, getAuthenticatedMediaUrlSync } from '../../utils/mediaUrl';
import { useAuth } from '../../contexts/AuthContext';

interface LightboxProps {
  imageUrl: string;
  onClose: () => void;
  pages?: Array<{ content: string; type: 'text' | 'image' | 'video' | 'audio' }>;
  currentPageIndex?: number;
  onPageChange?: (index: number) => void;
  content?: string;
  contentType?: 'text' | 'image' | 'video' | 'audio';
  onChange?: (value: string) => void;
  onConfirm?: (value: string) => void; // Called when OK is clicked (for edit confirmation)
  showConfirmButtons?: boolean; // Show OK/Cancel buttons for text editing
  onCondenseRequest?: (text: string) => Promise<string>; // Function to condense text and return result
  onCondense?: () => void; // Legacy: for node-level condense (creates new page)
  isCondensing?: boolean;
  // Gallery navigation (for asset browser)
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  // Action buttons
  onAddToStage?: () => void;
  onSetAsThumbnail?: () => void;
  showThumbnailButton?: boolean;
  // Asset management actions
  onDelete?: () => void;
  onDownload?: () => void;
  onSendToDevice?: () => void;
  onManageTags?: () => void; // eslint-disable-line @typescript-eslint/no-unused-vars
  onRename?: () => void;
  onDuplicate?: () => void;
  onCopyToClipboard?: () => void;
  // Title display
  title?: string;
}

/** Ctrl/Cmd+S — the editor's quick-save, the one shortcut the lightbox lets through. */
const isSaveShortcut = (e: { ctrlKey: boolean; metaKey: boolean; key: string }) =>
  (e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S');

export const Lightbox: React.FC<LightboxProps> = ({ 
  imageUrl, 
  onClose,
  pages = [],
  currentPageIndex = 0,
  onPageChange,
  content,
  contentType,
  onChange,
  onConfirm,
  showConfirmButtons = false,
  onCondenseRequest,
  onCondense,
  isCondensing: externalIsCondensing = false,
  // Gallery navigation
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  // Action buttons
  onAddToStage,
  onSetAsThumbnail,
  showThumbnailButton = false,
  // Asset management actions
  onDelete,
  onDownload,
  onSendToDevice,
  onManageTags: _onManageTags,
  onRename,
  onDuplicate,
  onCopyToClipboard,
  // Title
  title
}) => {
  const { user } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(currentPageIndex);
  const hasMultiplePages = pages.length > 1;
  
  // Track pending edit value for OK/Cancel functionality
  const initialContent = pages.length > 0 ? pages[currentIndex]?.content : (content || imageUrl);
  const [editValue, setEditValue] = useState(initialContent);
  const [isCondensing, setIsCondensing] = useState(false);
  
  // Context menu state for right-click
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Zoom/pan handled by react-zoom-pan-pinch; we keep only what the chrome needs.
  const [isHoveringImage, setIsHoveringImage] = useState(false);
  const [currentScale, setCurrentScale] = useState(1);
  const imageRef = useRef<HTMLImageElement>(null);
  const transformRef = useRef<ReactZoomPanPinchContentRef>(null);
  // Distinguish click from drag via mouse-down position (library's onPanning
  // fires on every mousemove — even 1px tremors — so a boolean "did pan" flag
  // is too aggressive and eats real clicks).
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);
  const CLICK_THRESHOLD_PX_SQ = 25;

  // Image natural dims drive the percentage readout + natural-px tooltip.
  const [imageNaturalSize, setImageNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [fitScale, setFitScale] = useState(1);

  // Recompute the "fit" scale (natural → viewport). Library scale multiplies this.
  const recalcFitScale = useCallback(() => {
    const img = imageRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const scale = Math.min(vw / img.naturalWidth, vh / img.naturalHeight, 1);
    setFitScale(scale);
    setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
  }, []);

  useEffect(() => {
    setImageNaturalSize(null);
    setFitScale(1);
    setCurrentScale(1);
  }, [currentIndex, imageUrl]);

  useEffect(() => {
    const handleResize = () => recalcFitScale();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [recalcFitScale]);
  
  // Reset edit value when content changes
  useEffect(() => {
    const newContent = pages.length > 0 ? pages[currentIndex]?.content : (content || imageUrl);
    setEditValue(newContent);
  }, [currentIndex, pages, content, imageUrl]);
  
  // Handle condense within the modal
  const handleCondenseInModal = useCallback(async () => {
    if (!onCondenseRequest || !editValue?.trim()) return;
    
    setIsCondensing(true);
    try {
      const condensedText = await onCondenseRequest(editValue);
      setEditValue(condensedText);
    } catch (error) {
      console.error('Failed to condense text:', error);
    } finally {
      setIsCondensing(false);
    }
  }, [editValue, onCondenseRequest]);
  
  const handleZoomIn = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    transformRef.current?.zoomIn();
  }, []);

  const handleZoomOut = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    transformRef.current?.zoomOut();
  }, []);

  const handleResetZoom = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    transformRef.current?.resetTransform();
  }, []);

  const handleMouseDownCapture = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const wasDrag = (e: React.MouseEvent): boolean => {
    const start = mouseDownPosRef.current;
    mouseDownPosRef.current = null;
    if (!start) return false;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    return dx * dx + dy * dy > CLICK_THRESHOLD_PX_SQ;
  };

  // Single-click on the image: zoom in at cursor if at 1×, else reset.
  const handleImageClick = useCallback((e: React.MouseEvent<HTMLImageElement>) => {
    if (wasDrag(e)) return;
    e.stopPropagation();
    const ref = transformRef.current;
    if (!ref) return;
    const { scale, positionX, positionY } = ref.state;
    if (scale > 1) {
      ref.resetTransform();
      return;
    }
    const targetScale = 2.5;
    const wrapperEl = (e.currentTarget.closest('.react-transform-wrapper') as HTMLElement | null);
    if (wrapperEl) {
      const rect = wrapperEl.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const newPosX = cx - (cx - positionX) * (targetScale / scale);
      const newPosY = cy - (cy - positionY) * (targetScale / scale);
      ref.setTransform(newPosX, newPosY, targetScale, 200);
    } else {
      ref.zoomIn();
    }
  }, []);

  // Close when clicking the dark margin around the image; suppress if drag.
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    if (wasDrag(e)) return;
    onClose();
  }, [onClose]);
  
  // Helper to detect if content is an image URL
  const isImageUrl = (url: string): boolean => {
    if (!url) return false;
    // Check for data URLs, blob URLs, or common image extensions
    // Note: regex allows .ext followed by ?, &, or end of string for query params
    return url.startsWith('data:image') || 
           url.startsWith('blob:') || 
           /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)([?&]|$)/i.test(url) ||
           url.includes('/image') ||
           url.includes('%2Fimage') ||  // URL-encoded path
           (url.startsWith('/api/media/proxy') && url.includes('key='));  // Media proxy URLs are images/videos
  };
  
  // Get current page content and type
  const currentPage = pages.length > 0 ? pages[currentIndex] : null;
  const rawContent = currentPage ? currentPage.content : (content || imageUrl);
  const currentType = currentPage ? currentPage.type : (contentType || (isImageUrl(rawContent) ? 'image' : 'text'));
  
  // Add authentication to media URLs
  const currentContent = useMemo(() => {
    if (currentType === 'image' || currentType === 'video') {
      const normalized = normalizeMediaUrl(rawContent);
      return getAuthenticatedMediaUrlSync(normalized, user?.id || null);
    }
    return rawContent;
  }, [rawContent, currentType, user?.id]);

  // Handle confirm (OK button)
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(editValue);
    } else if (onChange) {
      onChange(editValue);
    }
    onClose();
  };

  // Handle cancel (close without saving)
  const handleCancel = () => {
    // Reset to original value and close
    setEditValue(rawContent);
    onClose();
  };

  // The lightbox owns the keyboard while it is open. It is often mounted from
  // INSIDE a React Flow node (ImagePanel, PagedOutput), and every key used to
  // reach the canvas as well: ← / → moved the selected node (NodeWrapper's
  // onKeyDown — the node keeps focus after the click that opened us, and a key
  // from inside the portal bubbles back to it through the React tree), Delete
  // removed it (React Flow's document-level useKeyPress), and Escape / Ctrl+C /
  // Ctrl+Enter hit CreativeNodeFlow's document handler. A window CAPTURE
  // listener runs before all of those — document listeners, React's root
  // listener and the focused node itself — so stopping propagation here covers
  // every entry point. Keys typed into an editable field inside the lightbox
  // pass through (the text editor needs them, and the canvas handlers already
  // ignore input targets); the root's onKeyDown cuts their React-tree bubbling.
  // A ref keeps the listener registered once while always seeing fresh state.
  const keyHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyHandlerRef.current = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    const isEditable = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
    const insideLightbox = !!target && !!rootRef.current?.contains(target);

    // ESC closes the context menu if one is open, otherwise acts as cancel
    if (e.key === 'Escape') {
      e.stopPropagation();
      e.preventDefault();
      if (contextMenu) {
        setContextMenu(null);
      } else {
        handleCancel();
      }
      return;
    }

    if (isEditable && insideLightbox) return;
    // Ctrl/Cmd+S is the one canvas shortcut that stays live, so the editor's
    // document-level quick-save still runs. preventDefault here as well: if
    // anything stops the event before `document`, the browser must still not
    // open its own "Save page" dialog. stopKeyAtLightbox lets it through too.
    if (isSaveShortcut(e)) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();

    // Arrow keys navigate pages or the gallery; nothing else may move
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (hasMultiplePages && currentIndex > 0) {
        handlePreviousPage();
      } else if (onPrev && hasPrev) {
        onPrev();
      }
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (hasMultiplePages && currentIndex < pages.length - 1) {
        handleNextPage();
      } else if (onNext && hasNext) {
        onNext();
      }
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === ' ') {
      // Space on a focused lightbox button must still press it (close, arrows,
      // zoom, OK/Cancel); it only gets blocked from scrolling or reaching the canvas.
      const onOwnButton = e.key === ' ' && insideLightbox && !!target?.closest('button, [role="button"]');
      if (!onOwnButton) e.preventDefault();
    } else if (currentType === 'image') {
      // Zoom keyboard shortcuts for images: +/=/−/0
      if (e.key === '+' || e.key === '=') {
        transformRef.current?.zoomIn();
      } else if (e.key === '-' || e.key === '_') {
        transformRef.current?.zoomOut();
      } else if (e.key === '0') {
        transformRef.current?.resetTransform();
      }
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => keyHandlerRef.current(e);
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  // Take focus on open so the node that was clicked stops being the focused
  // element, and hand it back on close. Skipped when a child (the text editor)
  // already claimed focus.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const root = rootRef.current;
    if (root && !root.contains(document.activeElement)) {
      root.focus({ preventScroll: true });
    }
    return () => {
      if (previouslyFocused?.isConnected && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, []);

  // Close context menu when clicking outside, pressing Escape, or scrolling
  useEffect(() => {
    const closeMenu = () => setContextMenu(null);
    const handleClickOutside = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    
    if (contextMenu) {
      // Use capture phase to ensure this runs before other handlers
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('scroll', closeMenu, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
        document.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('scroll', closeMenu, true);
      };
    }
    return undefined;
  }, [contextMenu]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handlePreviousPage = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      onPageChange?.(newIndex);
    }
  };

  const handleNextPage = () => {
    if (currentIndex < pages.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      onPageChange?.(newIndex);
    }
  };

  // The portal moves us out of the node in the DOM, but React still bubbles
  // synthetic events through the component tree — back into the node wrapper
  // (selection, React Flow's onClick/onKeyDown) and whatever mounted us.
  // Nothing that happens inside the lightbox belongs to the canvas.
  const stopAtLightbox = (e: React.SyntheticEvent) => e.stopPropagation();
  // The lightbox is portaled to document.body, so stopping a native keydown at
  // its root also keeps it from `document`, where the editor's quick-save
  // listens. Everything else stays cut; Ctrl/Cmd+S goes through.
  const stopKeyAtLightbox = (e: React.KeyboardEvent) => {
    if (isSaveShortcut(e)) return;
    e.stopPropagation();
  };

  const lightboxContent = (
    <Box
      ref={rootRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      className="lightbox-root nodrag nopan nowheel"
      onKeyDown={stopKeyAtLightbox}
      onKeyUp={stopAtLightbox}
      onMouseDown={stopAtLightbox}
      onMouseUp={stopAtLightbox}
      onPointerDown={stopAtLightbox}
      onPointerUp={stopAtLightbox}
      onDoubleClick={stopAtLightbox}
      onContextMenu={stopAtLightbox}
      onWheel={stopAtLightbox}
      onTouchStart={stopAtLightbox}
      sx={{
        outline: 'none',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
        cursor: 'default',
      }}
      onClick={(e) => {
        e.stopPropagation();
        // Close when clicking directly on the backdrop (not on children)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={handleCancel}
        title="Cancel (Esc)"
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 10,
          color: 'white',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Title display */}
      {title && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: '50%',
            zIndex: 10,
            transform: 'translateX(-50%)',
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            maxWidth: '60vw',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {title}
        </Box>
      )}

      {/* Action buttons removed - using context menu instead */}

      {/* Zoom controls for images — show on hover, or whenever zoomed in */}
      {currentType === 'image' && (isHoveringImage || currentScale > 1) && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            display: 'flex',
            gap: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '8px',
            padding: '8px',
            zIndex: 10,
            transition: 'opacity 0.2s ease',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            onClick={handleZoomOut}
            disabled={currentScale <= 1}
            title="Zoom out (−)"
            size="small"
            sx={{
              color: currentScale <= 1 ? 'rgba(255, 255, 255, 0.3)' : 'white',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            <RemoveIcon />
          </IconButton>
          <Box
            sx={{
              color: 'white',
              fontSize: '14px',
              minWidth: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={imageNaturalSize ? `${imageNaturalSize.width} × ${imageNaturalSize.height} px` : undefined}
          >
            {Math.round(currentScale * fitScale * 100)}%
          </Box>
          <IconButton
            onClick={handleZoomIn}
            disabled={currentScale >= 5}
            title="Zoom in (+)"
            size="small"
            sx={{
              color: currentScale >= 5 ? 'rgba(255, 255, 255, 0.3)' : 'white',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            <AddIcon />
          </IconButton>
          <IconButton
            onClick={handleResetZoom}
            disabled={currentScale === 1}
            title="Reset zoom (0)"
            size="small"
            sx={{
              color: currentScale === 1 ? 'rgba(255, 255, 255, 0.3)' : 'white',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            <CenterFocusStrongIcon />
          </IconButton>
        </Box>
      )}

      {/* Previous button - for pages or gallery.
          Every piece of chrome needs an explicit zIndex: the image viewer's
          wrapper below is a full-viewport `position: relative` +
          `transform` box, so any z-index:auto sibling that precedes it in the
          DOM is painted UNDER it. The previous arrow was: hovering it showed
          no pointer, and a click landed on the wrapper's backdrop handler and
          closed the lightbox. */}
      {((hasMultiplePages && currentIndex > 0) || (onPrev && hasPrev)) && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            if (hasMultiplePages && currentIndex > 0) {
              handlePreviousPage();
            } else if (onPrev) {
              onPrev();
            }
          }}
          sx={{
            position: 'absolute',
            left: 16,
            top: '50%',
            zIndex: 10,
            cursor: 'pointer',
            transform: 'translateY(-50%)',
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            width: 48,
            height: 48,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
          }}
          title={hasMultiplePages ? "Previous page (← Arrow)" : "Previous (← Arrow)"}
        >
          <ChevronLeftIcon sx={{ fontSize: 32 }} />
        </IconButton>
      )}

      {/* Content display */}
      {currentType === 'image' ? (
        <TransformWrapper
          key={`${imageUrl}-${currentIndex}`}
          ref={transformRef}
          initialScale={1}
          minScale={1}
          maxScale={5}
          limitToBounds
          centerZoomedOut
          centerOnInit
          smooth
          doubleClick={{ disabled: true }}
          wheel={{ step: 0.15 }}
          pinch={{ step: 5 }}
          panning={{ velocityDisabled: false }}
          onTransform={(_, state) => setCurrentScale(state.scale)}
        >
            <TransformComponent
              wrapperStyle={{ width: '100vw', height: '100vh' }}
              wrapperProps={{
                onClick: handleBackdropClick,
                onMouseEnter: () => setIsHoveringImage(true),
                onMouseLeave: () => setIsHoveringImage(false),
                onMouseDownCapture: handleMouseDownCapture,
                onContextMenu: (e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setContextMenu({ x: e.clientX, y: e.clientY });
                },
              }}
            >
              <Box
                component="img"
                ref={imageRef}
                src={currentContent}
                alt="Fullscreen"
                draggable={false}
                onLoad={recalcFitScale}
                onClick={handleImageClick}
                style={{
                  // Inline wins over the library's class-based `img { pointer-events: none }`.
                  pointerEvents: 'auto',
                  cursor: currentScale > 1 ? 'zoom-out' : 'zoom-in',
                }}
                sx={{
                  maxWidth: '100vw',
                  maxHeight: '100vh',
                  objectFit: 'contain',
                  userSelect: 'none',
                }}
              />
            </TransformComponent>
        </TransformWrapper>
      ) : currentType === 'video' ? (
        <Box
          component="video"
          src={currentContent}
          controls
          autoPlay
          loop
          onClick={(e) => e.stopPropagation()}
          sx={{
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            cursor: 'default',
            borderRadius: '8px',
          }}
        />
      ) : currentType === 'audio' ? (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '40px',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: '8px',
          }}
        >
          {title && (
            <Box
              component="h3"
              sx={{
                marginTop: 0,
                marginBottom: '24px',
                color: 'white',
                fontSize: '18px',
                fontWeight: 500,
              }}
            >
              {title}
            </Box>
          )}
          <Box
            component="audio"
            src={currentContent}
            controls
            autoPlay
            sx={{
              minWidth: '400px',
            }}
          />
        </Box>
      ) : (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '800px',
            backgroundColor: 'var(--color-bg-primary)',
            borderRadius: '8px',
            overflow: 'hidden',
            cursor: 'default',
            display: 'flex',
            flexDirection: 'column',
            fontSize: '16px',
            lineHeight: '1.6',
            '& .markdown-textarea-preview': {
              fontSize: '16px !important',
              lineHeight: '1.6 !important'
            }
          }}
        >
          {/* Scrollable text content area */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              padding: '24px',
              minHeight: 0, // Important for flex shrinking
              display: 'flex',
              flexDirection: 'column',
              // Ensure textarea and preview fill the available space
              '& .markdown-textarea-container': {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              },
              '& .markdown-textarea-edit-wrapper': {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              },
              '& .markdown-textarea-edit': {
                flex: 1,
                minHeight: '60vh !important',
                fontSize: '16px !important',
                lineHeight: '1.6 !important',
              },
              '& .markdown-textarea-preview': {
                flex: 1,
                minHeight: '60vh',
              },
            }}
          >
            <MarkdownTextarea
              value={editValue}
              onChange={(val) => setEditValue(val)}
              placeholder=""
              variant="neutral"
              readOnly={!showConfirmButtons && !onChange}
              showSubmitButton={false}
            />
          </Box>
          
          {/* Fixed action bar at the bottom */}
          {(showConfirmButtons || onCondenseRequest || onCondense) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: showConfirmButtons ? 'flex-end' : 'flex-start',
                alignItems: 'center',
                gap: '12px',
                padding: '16px 24px',
                backgroundColor: 'var(--color-bg-primary)',
                borderTop: '1px solid var(--color-border)',
                flexShrink: 0,
              }}
            >
              {/* Condense button - left side */}
              {(onCondenseRequest || onCondense) && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onCondenseRequest) {
                      handleCondenseInModal();
                    } else if (onCondense) {
                      onCondense();
                    }
                  }}
                  disabled={isCondensing || externalIsCondensing || !editValue}
                  title={isCondensing ? "Condensing..." : "Condense text"}
                  sx={{
                    marginRight: showConfirmButtons ? 'auto' : 0,
                    color: (isCondensing || externalIsCondensing) ? 'var(--color-text-disabled)' : 'var(--color-accent-primary)',
                    backgroundColor: 'rgba(var(--color-accent-primary-rgb), 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(var(--color-accent-primary-rgb), 0.2)',
                    },
                    '&.Mui-disabled': {
                      color: 'var(--color-text-disabled)',
                    },
                  }}
                >
                  {isCondensing ? (
                    <CircularProgress size={18} sx={{ color: 'var(--color-accent-primary)' }} />
                  ) : (
                    <CompressIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              )}
              
              {/* Cancel and OK buttons for editable content */}
              {showConfirmButtons && (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{
                      color: 'var(--color-text-secondary)',
                      borderColor: 'var(--color-border)',
                      '&:hover': {
                        borderColor: 'var(--color-text-secondary)',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirm}
                    sx={{
                      backgroundColor: 'var(--color-accent-primary)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'var(--color-accent-primary-hover)',
                      },
                    }}
                  >
                    OK
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Next button - for pages or gallery */}
      {((hasMultiplePages && currentIndex < pages.length - 1) || (onNext && hasNext)) && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            if (hasMultiplePages && currentIndex < pages.length - 1) {
              handleNextPage();
            } else if (onNext) {
              onNext();
            }
          }}
          sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            zIndex: 10,
            cursor: 'pointer',
            transform: 'translateY(-50%)',
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            width: 48,
            height: 48,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
          }}
          title={hasMultiplePages ? "Next page (→ Arrow)" : "Next (→ Arrow)"}
        >
          <ChevronRightIcon sx={{ fontSize: 32 }} />
        </IconButton>
      )}

      {/* Page indicator - hide when zoomed */}
      {hasMultiplePages && currentScale === 1 && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 10,
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 500
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {currentIndex + 1} / {pages.length}
        </Box>
      )}

      {/* Context menu for images */}
      {contextMenu && currentType === 'image' && createPortal(
        <div
          ref={contextMenuRef}
          className="lightbox-context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 10001,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {onRename && (
            <button
              className="context-menu-item"
              onClick={() => {
                onRename();
                setContextMenu(null);
              }}
            >
              <EditIcon fontSize="small" />
              <span>Rename</span>
            </button>
          )}
          {onSendToDevice && (
            <button
              className="context-menu-item"
              onClick={() => {
                onSendToDevice();
                setContextMenu(null);
              }}
            >
              <PhoneIphoneIcon fontSize="small" />
              <span>Send to Device</span>
            </button>
          )}
          {onCopyToClipboard && (
            <button
              className="context-menu-item"
              onClick={() => {
                onCopyToClipboard();
                setContextMenu(null);
              }}
            >
              <ContentCopyIcon fontSize="small" />
              <span>Copy to Clipboard</span>
            </button>
          )}
          {onDownload && (
            <button
              className="context-menu-item"
              onClick={() => {
                onDownload();
                setContextMenu(null);
              }}
            >
              <DownloadIcon fontSize="small" />
              <span>Download</span>
            </button>
          )}
          {onDuplicate && (
            <button
              className="context-menu-item"
              onClick={() => {
                onDuplicate();
                setContextMenu(null);
              }}
            >
              <AddToPhotosIcon fontSize="small" />
              <span>Duplicate</span>
            </button>
          )}
          {(onRename || onSendToDevice || onDownload || onCopyToClipboard) && (onAddToStage || onSetAsThumbnail) && (
            <div className="context-menu-divider" />
          )}
          {onAddToStage && (
            <button
              className="context-menu-item"
              onClick={() => {
                onAddToStage();
                setContextMenu(null);
              }}
            >
              <AddToPhotosIcon fontSize="small" />
              <span>Place on Stage</span>
            </button>
          )}
          {showThumbnailButton && onSetAsThumbnail && (
            <button
              className="context-menu-item"
              onClick={() => {
                onSetAsThumbnail();
                setContextMenu(null);
              }}
            >
              <ImageIcon fontSize="small" />
              <span>Set Knod Thumbnail</span>
            </button>
          )}
          {onDelete && (
            <>
              <div className="context-menu-divider" />
              <button
                className="context-menu-item danger"
                onClick={() => {
                  onDelete();
                  setContextMenu(null);
                }}
              >
                <DeleteIcon fontSize="small" />
                <span>Delete</span>
              </button>
            </>
          )}
        </div>,
        document.body
      )}
    </Box>
  );

  return createPortal(lightboxContent, document.body);
};
